import { useState } from 'react';
import classNames from 'classnames';
import { useField } from 'formik';
import IconSVG from '../../../styles/svg-icons';
import { constants } from '../../../constants';
import { DropDownList, DropDownListItem } from '../../controls/DropDownList';
import { FormError } from '../../controls/FormError';

interface DropDownFieldWithAddNewProps {
    name: string;
    label: string;
    options: DropDownListItem[];
    placeholder?: string;
    required?: boolean;
    maxLength?: number;
    wrapperClassName?: string;
    addNewLabel?: string;
    withEmptyValue?: boolean;
    withSearch?: boolean;
}

const ADD_NEW_VALUE = '__add_new__';
const EMPTY_VALUE = '__empty__';

export function DropDownFieldWithAddNew({
    name,
    label,
    options,
    placeholder = 'Select',
    required,
    maxLength = 64,
    wrapperClassName,
    addNewLabel = 'Add New…',
    withEmptyValue = false,
    withSearch = false,
}: DropDownFieldWithAddNewProps) {
    const [field, meta, helpers] = useField<string | undefined>(name);
    const [addNewMode, setAddNewMode] = useState(false);
    const [draftValue, setDraftValue] = useState('');

    const items: DropDownListItem[] = [
        { value: ADD_NEW_VALUE, text: addNewLabel },
        ...(withEmptyValue ? [{ value: EMPTY_VALUE, text: constants.emptyPlaceholder, selected: !field.value }] : []),
        ...options.map(o => ({ ...o, selected: String(o.value) === field.value })),
    ];

    const handleSelect = (value: string) => {
        if (value === ADD_NEW_VALUE) {
            setAddNewMode(true);
            setDraftValue('');
            helpers.setValue(undefined);
            return;
        }
        if (value === EMPTY_VALUE) {
            helpers.setValue(undefined);
            return;
        }
        helpers.setValue(value || undefined);
    };

    const handleClose = () => {
        setAddNewMode(false);
        setDraftValue('');
        helpers.setValue(undefined);
    };

    const handleDraftChange = (value: string) => {
        setDraftValue(value);
        helpers.setValue(value || undefined);
    };

    const withError = meta.touched && meta.error;

    return (
        <div className={classNames('data-item-row', wrapperClassName)} onClick={e => e.stopPropagation()}>
            {label && (
                <label className="data-item-col data-item-col-title data-item-capitalize">
                    {label}
                    {required && <span className="text-red"> *</span>}
                </label>
            )}
            <div className="data-item-col">
                {addNewMode ? (
                    <div className="loan-add-new-row">
                        <input
                            className={classNames('form-control', { 'is-invalid': withError })}
                            value={draftValue}
                            maxLength={maxLength}
                            onChange={e => handleDraftChange(e.target.value)}
                            placeholder={placeholder}
                            autoFocus
                        />
                        <button
                            type="button"
                            className="btn-link loan-add-new-btn loan-add-new-btn-close"
                            onClick={handleClose}
                            aria-label="Close"
                        >
                            <IconSVG name="close" width={16} height={16} />
                        </button>
                    </div>
                ) : (
                    <DropDownList
                        className={classNames('form-control', { 'is-invalid': withError })}
                        items={items}
                        onChange={item => handleSelect(String(item.value))}
                        placeholder={placeholder}
                        withSearch={withSearch}
                        defaultSortingEnabled={false}
                    />
                )}
                {withError && <FormError message={meta.error} />}
            </div>
        </div>
    );
}
